// ./src/utils/dateHelpers.ts
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import duration from "dayjs/plugin/duration";

// Enable plugins
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

export const getScheduleDate = (date?: any) => {
  if (date) return date;

  // Get local date to avoid timezone issues
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const formatDuration = (seconds?: number): string => {
  if (!seconds || seconds <= 0) return "-";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

export const formatTime = (date: Date): string => {
  return dayjs(date).format("hh:mm A");
};

/* export const formatDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}; */

export const formatDate = (date?: string | Date | null) => {
  if (!date) return "-";

  return dayjs(date).format("MMM DD, YYYY");
};

export const formatDateWeekDay = (date?: string | Date | null) => {
  if (!date) return "-";

  return dayjs(date).format("ddd, MMM DD, YYYY");
};

/**
 * Format time from UTC string, optionally ignoring timezone conversion
 * @param utcTimeString - UTC time string like "1970-01-01T17:00:00.000+00:00"
 * @param format - Time format string
 * @param ignoreTimezone - If true, treats the time as-is without timezone conversion
 */
export const formatTimeFromUTC = (
  utcTimeString?: string | null,
  format: string = "h:mm A",
  ignoreTimezone: boolean = false
): string => {
  if (!utcTimeString) return "-";

  try {
    let time: dayjs.Dayjs;

    if (ignoreTimezone) {
      // Strip timezone info (Z or +00:00) to parse as local time
      // This prevents dayjs from treating it as UTC
      const timeWithoutTZ = utcTimeString
        .replace(/Z$/, "")
        .replace(/[+-]\d{2}:\d{2}$/, "");
      time = dayjs(timeWithoutTZ);
    } else {
      // Parse UTC time and convert to local timezone
      time = dayjs.utc(utcTimeString).local();
    }

    if (!time.isValid()) {
      return "-";
    }

    return time.format(format);
  } catch (error) {
    console.warn("Error formatting time:", error);
    return "-";
  }
};

/**
 * Helper function to format time range (legacy - kept for backwards compatibility)
 * For simple pre-formatted time strings
 */
export const formatTimeRange = (startTime?: string, endTime?: string) => {
  if (!startTime && !endTime) return "-";
  if (!endTime) return startTime || "-";
  if (!startTime) return endTime || "-";
  return `${startTime} - ${endTime}`;
};

export const formatDaysOfWeek = (
  daysOfWeek?: string,
  recurrencePattern?: string
) => {
  // Explicit checks to avoid falsy value issues
  if (
    recurrencePattern === undefined ||
    recurrencePattern === null ||
    recurrencePattern === ""
  ) {
    return "-";
  }

  if (daysOfWeek === undefined || daysOfWeek === null || daysOfWeek === "") {
    return "-";
  }

  const recurrencePatternUpper = recurrencePattern.toUpperCase();

  if (recurrencePatternUpper === "WEEKLY") {
    const dayNames: Record<string, string> = {
      "1": "Mon",
      "2": "Tue",
      "3": "Wed",
      "4": "Thu",
      "5": "Fri",
      "6": "Sat",
      "7": "Sun",
    };

    // Replace all "0" with "7" to make Sunday work naturally in sorting
    const normalizedDaysOfWeek = daysOfWeek.replace(/\b0\b/g, "7");

    // Parse days
    const parsedDays = normalizedDaysOfWeek
      .split(",")
      .map((day) => day.trim())
      .filter((day) => day in dayNames);

    if (parsedDays.length === 0) {
      return "-";
    }

    const daySet = new Set(parsedDays);

    // Check for all days
    if (daySet.size === 7) {
      return "All Days";
    }

    // Check for weekends (Sat=6, Sun=7)
    if (daySet.size === 2 && daySet.has("6") && daySet.has("7")) {
      return "Weekend";
    }

    // Check for weekdays (Mon-Fri = 1-5)
    if (
      daySet.size === 5 &&
      daySet.has("1") &&
      daySet.has("2") &&
      daySet.has("3") &&
      daySet.has("4") &&
      daySet.has("5")
    ) {
      return "Weekday";
    }

    // Sort by day number (now 1-7, so natural sorting works!)
    const sortedDays = Array.from(daySet)
      .map((day) => parseInt(day))
      .sort((a, b) => a - b);

    // Group consecutive days
    const groups: string[] = [];
    let i = 0;

    while (i < sortedDays.length) {
      const rangeStart = i;

      // Find consecutive days
      while (
        i < sortedDays.length - 1 &&
        sortedDays[i + 1] === sortedDays[i] + 1
      ) {
        i++;
      }

      const rangeEnd = i;
      const rangeLength = rangeEnd - rangeStart + 1;

      if (rangeLength >= 2) {
        // Range of 2 or more days
        groups.push(
          `${dayNames[sortedDays[rangeStart].toString()]}-${dayNames[sortedDays[rangeEnd].toString()]
          }`
        );
      } else {
        // Single day
        groups.push(dayNames[sortedDays[rangeStart].toString()]);
      }

      i++;
    }

    return groups.join(", ");
  } else if (recurrencePatternUpper === "MONTHLY") {
    // Parse monthly days
    const days = daysOfWeek
      .split(",")
      .map((day) => parseInt(day.trim()))
      .filter((day) => !isNaN(day) && day >= 1 && day <= 31)
      .sort((a, b) => a - b);

    if (days.length === 0) {
      return "-";
    }

    // Group consecutive days
    const groups: string[] = [];
    let i = 0;

    while (i < days.length) {
      const rangeStart = i;

      // Find consecutive days
      while (i < days.length - 1 && days[i + 1] === days[i] + 1) {
        i++;
      }

      const rangeEnd = i;
      const rangeLength = rangeEnd - rangeStart + 1;

      if (rangeLength >= 2) {
        // Range of 2 or more days
        groups.push(`${days[rangeStart]}-${days[rangeEnd]}`);
      } else {
        // Single day
        groups.push(days[rangeStart].toString());
      }

      i++;
    }

    return groups.join(", ");
  }

  return "-";
};

export type DateFormatType =
  | "relative"
  | "short"
  | "medium"
  | "long"
  | "full"
  | "time"
  | "timeShort"
  | "date"
  | "datetime"
  | "datetime2"
  | "iso";

/**
 * Format a date/time string using dayjs with different preset formats
 * @param dateInput - Date string, Date object, or dayjs instance
 * @param format - Format type or custom format string
 * @param timezone - Optional timezone (defaults to local)
 * @returns Formatted date string
 */
export const formatDateTime = (
  dateInput: string | Date | dayjs.Dayjs | null | undefined,
  format: DateFormatType | string = "medium",
  timezone?: string
): string => {
  if (!dateInput) return "-";

  try {
    let date = dayjs(dateInput);

    // Apply timezone if specified
    if (timezone) {
      date = date.tz(timezone);
    }

    // Check if date is valid
    if (!date.isValid()) {
      return "Invalid Date";
    }

    // Handle preset formats
    switch (format) {
      case "relative":
        return formatRelativeDateTime(date);

      case "short":
        return date.format("M/D/YY");

      case "medium":
        return date.format("MMM D, YYYY");

      case "long":
        return date.format("MMMM D, YYYY");

      case "full":
        return date.format("dddd, MMMM D, YYYY [@] h:mm A");

      case "time":
        return date.format("h:mm A");

      case "timeShort":
        return date.format("HH:mm");

      case "date":
        return date.format("YYYY-MM-DD");

      case "datetime":
        return date.format("MMM D, YYYY [@] h:mm A");

      case "datetime2":
        return date.format(`MMM D, YYYY<br/>h:mm A`);

      case "iso":
        return date.toISOString();

      default:
        // Treat as custom format string
        return date.format(format);
    }
  } catch (error) {
    console.warn("Error formatting date:", error);
    return "Invalid Date";
  }
};

/**
 * Format date/time in a user-friendly relative format.
 * This is the unified relative time formatting used across the app.
 * 
 * Rules:
 * - < 1 minute ago: "Just now"
 * - < 2 hours ago: "X minutes ago" or "X hour(s) ago"
 * - Today (but > 2 hours): "Today, 2:30 PM"
 * - Yesterday: "Yesterday, 10:15 AM"
 * - Older (same year): "Jan 15, 3:45 PM"
 * - Older (different year): "Jan 15, 2024, 3:45 PM"
 * 
 * @param date - dayjs instance to format
 * @returns Formatted relative date/time string
 */
const formatRelativeDateTime = (date: dayjs.Dayjs): string => {
  const now = dayjs();
  const diffMinutes = now.diff(date, "minute");
  const diffHours = now.diff(date, "hour");

  // Less than 1 minute ago
  if (diffMinutes < 1) {
    return "Just now";
  }

  // Less than 2 hours ago - use relative time
  if (diffHours < 2) {
    if (diffMinutes < 60) {
      return diffMinutes === 1 ? "1 minute ago" : `${diffMinutes} minutes ago`;
    }
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  }

  const startOfToday = now.startOf("day");
  const startOfYesterday = startOfToday.subtract(1, "day");
  const timeFormat = date.format("h:mm A");

  // Today (but more than 2 hours ago)
  if (date.isAfter(startOfToday)) {
    return `Today, ${timeFormat}`;
  }

  // Yesterday
  if (date.isAfter(startOfYesterday)) {
    return `Yesterday, ${timeFormat}`;
  }

  // Older - check if same year
  if (date.year() === now.year()) {
    return date.format("MMM D, h:mm A");
  }

  // Different year
  return date.format("MMM D, YYYY, h:mm A");
};

/**
 * Get relative time from now (e.g., "2 hours ago", "in 3 days")
 * @deprecated Use formatDateTime(date, "relative") instead for consistent formatting
 */
export const getRelativeTime = (
  dateInput: string | Date | dayjs.Dayjs | null | undefined
): string => {
  return formatDateTime(dateInput, "relative");
};

/**
 * Check if a date is today
 */
export const isToday = (
  dateInput: string | Date | dayjs.Dayjs | null | undefined
): boolean => {
  if (!dateInput) return false;
  const date = dayjs(dateInput);
  return date.isValid() && date.isSame(dayjs(), "day");
};

/**
 * Check if a date is in the past
 */
export const isPast = (
  dateInput: string | Date | dayjs.Dayjs | null | undefined
): boolean => {
  if (!dateInput) return false;
  const date = dayjs(dateInput);
  return date.isValid() && date.isBefore(dayjs());
};

/**
 * Check if a date is in the future
 */
export const isFuture = (
  dateInput: string | Date | dayjs.Dayjs | null | undefined
): boolean => {
  if (!dateInput) return false;
  const date = dayjs(dateInput);
  return date.isValid() && date.isAfter(dayjs());
};

/**
 * Get duration between two dates in human readable format
 * Returns compact format like "1d 2h 30m"
 */
export const getDuration = (
  startDate: string | Date | dayjs.Dayjs,
  endDate: string | Date | dayjs.Dayjs
): string => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) {
    return "Invalid dates";
  }

  const duration = end.diff(start);
  const durationObj = dayjs.duration(duration);

  const days = Math.floor(durationObj.asDays());
  const hours = durationObj.hours();
  const minutes = durationObj.minutes();

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

/**
 * Format duration between two dates in natural language
 * Returns null if dates are invalid, zero, or negative duration
 * 
 * @example
 * formatDurationBetween("2025-01-01T08:00:00", "2025-01-01T08:45:00") // "45 min"
 * formatDurationBetween("2025-01-01T08:00:00", "2025-01-01T09:00:00") // "1 hour"
 * formatDurationBetween("2025-01-01T08:00:00", "2025-01-01T10:30:00") // "2 hours 30 min"
 * formatDurationBetween("2025-01-01T08:00:00", "2025-01-02T10:00:00") // "1 day 2 hours"
 */
export const formatDurationBetween = (
  startDate: string | Date | dayjs.Dayjs | null | undefined,
  endDate: string | Date | dayjs.Dayjs | null | undefined
): string | null => {
  if (!startDate || !endDate) return null;

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  const diffMs = end.diff(start);

  // Return null for zero or negative duration
  if (diffMs <= 0) return null;

  const durationObj = dayjs.duration(diffMs);
  const totalMinutes = Math.floor(durationObj.asMinutes());

  if (totalMinutes === 0) return null;

  const days = Math.floor(durationObj.asDays());
  const hours = durationObj.hours();
  const minutes = durationObj.minutes();

  // Build parts array for natural formatting
  const parts: string[] = [];

  if (days > 0) {
    parts.push(days === 1 ? "1 day" : `${days} days`);
  }

  if (hours > 0) {
    parts.push(hours === 1 ? "1 hour" : `${hours} hours`);
  }

  if (minutes > 0 && days === 0) {
    // Only show minutes if less than a day
    parts.push(`${minutes} min`);
  }

  // If we have days but no hours and no minutes shown, still valid
  if (parts.length === 0) {
    return null;
  }

  // Join with space (e.g., "2 hours 30 min" or "1 day 3 hours")
  return parts.join(" ");
};

/**
 * Calculate distance between two odometer readings
 * Returns null if either value is missing or result is not positive
 */
export const calculateOdometerDistance = (
  prevOdometer: number | undefined | null,
  currentOdometer: number | undefined | null
): number | null => {
  if (
    prevOdometer === undefined ||
    prevOdometer === null ||
    currentOdometer === undefined ||
    currentOdometer === null
  ) {
    return null;
  }

  const distance = currentOdometer - prevOdometer;
  return distance > 0 ? distance : null;
};

/**
 * Check if a date matches the recurrence pattern
 */
export const matchesRecurrence = (
  checkDate: dayjs.Dayjs,
  recurrencePattern?: string,
  recurrenceDays?: string
): boolean => {
  if (!recurrencePattern || recurrencePattern === "NONE") {
    return true;
  }

  if (!recurrenceDays) {
    return true;
  }

  const days = recurrenceDays.split(",").map((d) => parseInt(d.trim(), 10));

  switch (recurrencePattern) {
    case "WEEKLY":
      // For weekly: 0=Sunday, 1=Monday, ..., 6=Saturday
      let dayOfWeek: number = checkDate.day();

      if (dayOfWeek == 0) {
        dayOfWeek = 7;
      }

      return days.includes(dayOfWeek);

    case "MONTHLY":
      // For monthly: 1-31 days of month
      const dayOfMonth = checkDate.date();
      return days.includes(dayOfMonth);

    case "YEARLY":
      // For yearly recurrenceDays is a day in YYYY-MM-DD
      const date = dayjs(recurrenceDays);
      return date.isSame(checkDate);

    default:
      return true;
  }
};

/**
 * Format UTC date string to local date (date only, no time)
 * Converts "2025-10-01T06:00:00.000+00:00" to local date format
 */
export const formatUTCDateToLocal = (
  utcDateString?: string | null,
  format: string = "MMM D, YYYY"
): string => {
  if (!utcDateString) return "-";

  try {
    // Parse UTC date and convert to local
    const localDate = dayjs.utc(utcDateString).local();

    if (!localDate.isValid()) {
      return "-";
    }

    return localDate.format(format);
  } catch (error) {
    console.warn("Error formatting UTC date:", error);
    return "-";
  }
};

/**
 * Get current local date/time as string for DateTimePicker
 */
export const getCurrentLocalDateTimeString = (): string => {
  return dayjs().format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Format UTC time string to local time (time only, no date)
 * Converts "1970-01-01T17:00:00.000+00:00" to local time format
 * @param utcTimeString - UTC time string
 * @param format - Time format string
 * @param ignoreTimezone - If true, treats the time as-is without timezone conversion
 */
export const formatUTCTimeToLocal = (
  utcTimeString?: string | null,
  format: string = "h:mm A",
  ignoreTimezone: boolean = false
): string => {
  return formatTimeFromUTC(utcTimeString, format, ignoreTimezone);
};

/**
 * Format date range from UTC dates with smart formatting
 * Returns formatted string like:
 * - "Oct 10 - 20, 2025" (same month and year)
 * - "Oct 10 - Nov 5, 2025" (same year, different months)
 * - "Dec 20, 2024 - Jan 5, 2025" (different years)
 * - "Oct 1, 2025" (no end date or same date)
 */
export const formatUTCDateRange = (
  startDate?: string | null,
  endDate?: string | null
): string => {
  if (!startDate && !endDate) return "-";

  try {
    const startLocal = startDate ? dayjs.utc(startDate).local() : null;
    const endLocal = endDate ? dayjs.utc(endDate).local() : null;

    // If only start date exists
    if (startLocal && !endLocal) {
      return startLocal.format("MMM D, YYYY");
    }

    // If only end date exists
    if (!startLocal && endLocal) {
      return `Until ${endLocal.format("MMM D, YYYY")}`;
    }

    // Both dates exist
    if (startLocal && endLocal) {
      // Check if dates are invalid
      if (!startLocal.isValid() || !endLocal.isValid()) {
        return "-";
      }

      // If same date
      if (startLocal.isSame(endLocal, "day")) {
        return startLocal.format("MMM D, YYYY");
      }

      const sameYear = startLocal.year() === endLocal.year();
      const sameMonth = sameYear && startLocal.month() === endLocal.month();

      // Same month and year: "Oct 10 - 20, 2025"
      if (sameMonth) {
        return `${startLocal.format("MMM D")} - ${endLocal.format("D, YYYY")}`;
      }

      // Same year, different months: "Oct 10 - Nov 5, 2025"
      if (sameYear) {
        return `${startLocal.format("MMM D")} - ${endLocal.format(
          "MMM D, YYYY"
        )}`;
      }

      // Different years: "Dec 20, 2024 - Jan 5, 2025"
      return `${startLocal.format("MMM D, YYYY")} - ${endLocal.format(
        "MMM D, YYYY"
      )}`;
    }

    return "-";
  } catch (error) {
    console.warn("Error formatting UTC date range:", error);
    return "-";
  }
};

/**
 * Format time range from UTC times
 * Returns formatted string like "9:00 AM - 5:00 PM" or "9:00 AM" if no end time
 * @param startTime - UTC start time string
 * @param endTime - UTC end time string
 * @param format - Time format string
 * @param ignoreTimezone - If true, treats times as-is without timezone conversion
 */
export const formatUTCTimeRange = (
  startTime?: string | null,
  endTime?: string | null,
  format: string = "h:mm A",
  ignoreTimezone: boolean = false
): string => {
  const start = formatTimeFromUTC(startTime, format, ignoreTimezone);
  const end = formatTimeFromUTC(endTime, format, ignoreTimezone);

  if (start === "-" && end === "-") return "-";
  if (end === "-") return start;
  if (start === "-") return `Until ${end}`;
  if (start === end) return start;

  return `${start} - ${end}`;
};

/**
 * Convert local time string from DateTimePicker to UTC ISO string for API
 */
export const localStringToUtc = (
  localDateString: string | null
): string | null => {
  if (!localDateString) return null;
  try {
    const localDate = dayjs(localDateString);
    if (!localDate.isValid()) return null;
    return localDate.utc().toISOString();
  } catch {
    return null;
  }
};

/**
 * Convert UTC date string from API to local time string for DateTimePicker
 * DateTimePicker expects format like "YYYY-MM-DD HH:mm"
 */
export const utcToLocalString = (
  utcDateString: string | null | undefined
): string | null => {
  if (!utcDateString) return null;
  try {
    // Parse as UTC, convert to local, format for DateTimePicker
    const localDate = dayjs.utc(utcDateString).local();
    if (!localDate.isValid()) return null;
    return localDate.format("YYYY-MM-DD HH:mm");
  } catch {
    return null;
  }
};

/**
 * Convert date strings (YYYY-MM-DD) to UTC ISO strings for API calls.
 * - dateFrom: start of day (00:00:00) in user's local timezone → UTC
 * - dateTo: end of day (23:59:59.999) in user's local timezone → UTC
 */
export const toApiDateRange = (
  dateFrom: string | null,
  dateTo: string | null
): { dateFrom: string | null; dateTo: string | null } => {
  return {
    dateFrom: dateFrom
      ? dayjs(dateFrom).startOf("day").utc().toISOString()
      : null,
    dateTo: dateTo ? dayjs(dateTo).endOf("day").utc().toISOString() : null,
  };
};

/**
 * Format short date for compact display
 * Returns "Oct 1" or "Oct 1, 2025" if different year
 */
export const formatUTCDateShort = (utcDateString?: string | null): string => {
  if (!utcDateString) return "-";

  try {
    const localDate = dayjs.utc(utcDateString).local();

    if (!localDate.isValid()) {
      return "-";
    }

    // If same year, show month and day only
    if (localDate.year() === dayjs().year()) {
      return localDate.format("MMM D");
    }

    // Different year, show year too
    return localDate.format("MMM D, YYYY");
  } catch (error) {
    console.warn("Error formatting UTC date short:", error);
    return "-";
  }
};

// Helper functions using dayjs
export const apiDateStringToDate = (
  dateString: string | undefined
): Date | null => {
  if (!dateString) return null;
  const parsed = dayjs(dateString, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.toDate() : null;
};

export const dateToApiDateString = (date: Date | null): string | undefined => {
  if (!date) return undefined;
  return dayjs(date).format("YYYY-MM-DD");
};

/**
 * Convert API time format to form time format (HH:mm)
 * API format: "1970-01-01T17:00:00.000Z" or legacy "10:00" or "22:00"
 * Form format: "17:00" (24-hour HH:mm)
 */
export const apiTimeToFormTime = (apiTime: string | undefined): string => {
  if (!apiTime) return "";

  // Check if it's the new ISO format with timezone
  if (apiTime.includes("T") && apiTime.includes("Z")) {
    // Strip the Z to prevent timezone conversion
    const timeWithoutTZ = apiTime
      .replace(/Z$/, "")
      .replace(/[+-]\d{2}:\d{2}$/, "");
    const parsed = dayjs(timeWithoutTZ);
    if (parsed.isValid()) {
      return parsed.format("HH:mm");
    }
  }

  // Legacy format or already in HH:mm format
  // Try to parse as time
  const formats = ["HH:mm", "H:mm", "hh:mm A", "h:mm A"];
  for (const format of formats) {
    const parsed = dayjs(apiTime, format, true);
    if (parsed.isValid()) {
      return parsed.format("HH:mm");
    }
  }

  return apiTime; // Return as-is if can't parse
};

/**
 * Convert form time format to API time format
 * Form format: "17:00" (24-hour HH:mm from TimeInput)
 * API format: "1970-01-01T17:00:00.000Z"
 * IMPORTANT: Does NOT convert timezone - just formats as UTC string
 */
export const formTimeToApiTime = (formTime: string): string => {
  if (!formTime) return "";

  // Parse the time (TimeInput gives us HH:mm format)
  const parsed = dayjs(formTime, "HH:mm", true);
  if (!parsed.isValid()) {
    // Try other formats just in case
    const formats = ["H:mm", "hh:mm A", "h:mm A"];
    for (const format of formats) {
      const attemptParsed = dayjs(formTime, format, true);
      if (attemptParsed.isValid()) {
        // Create UTC ISO string WITHOUT timezone conversion
        const hour = String(attemptParsed.hour()).padStart(2, "0");
        const minute = String(attemptParsed.minute()).padStart(2, "0");
        return `1970-01-01T${hour}:${minute}:00.000Z`;
      }
    }
    return formTime; // Return as-is if can't parse
  }

  // Create UTC ISO string WITHOUT timezone conversion
  // Just format the time values directly into the ISO string
  const hour = String(parsed.hour()).padStart(2, "0");
  const minute = String(parsed.minute()).padStart(2, "0");
  return `1970-01-01T${hour}:${minute}:00.000Z`;
};

// Helper function to validate time format (HH:mm from TimeInput)
export const isValidTimeFormat = (timeString: string): boolean => {
  if (!timeString) return false;

  // TimeInput primarily outputs HH:mm format
  const formats = [
    "HH:mm", // 24-hour format
    "H:mm", // 24-hour format without leading zero
    "hh:mm A", // 12-hour format with AM/PM
    "h:mm A", // 12-hour format without leading zero
  ];

  return formats.some((format) => dayjs(timeString, format, true).isValid());
};

export const compareTimeStrings = (
  startTime: string,
  endTime: string
): boolean => {
  if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
    return false;
  }

  // Parse times using dayjs with multiple format support
  const parseTime = (timeStr: string) => {
    const formats = ["HH:mm", "H:mm", "hh:mm A", "h:mm A"];
    for (const format of formats) {
      const parsed = dayjs(timeStr, format, true);
      if (parsed.isValid()) {
        return parsed;
      }
    }
    return null;
  };

  const startParsed = parseTime(startTime);
  const endParsed = parseTime(endTime);

  if (!startParsed || !endParsed) return false;

  return endParsed.isAfter(startParsed);
};


// Add this type near the top with other types
export type PeriodComparisonType =
  | "previous"
  | "previousMonth"
  | "sameMonthLastYear"
  | "custom"
  | "none";


/**
 * Convert a date (string or Date) to local date string for DatePickerInput
 * DatePickerInput expects format "YYYY-MM-DD"
 */
export const toDatePickerString = (
  date: string | Date | null | undefined
): string | null => {
  if (!date) return null;
  try {
    const d = dayjs(date);
    if (!d.isValid()) return null;
    return d.format("YYYY-MM-DD");
  } catch {
    return null;
  }
};

/**
 * Convert DatePickerInput string (YYYY-MM-DD) to ISO string
 */
export const datePickerStringToIso = (
  dateStr: string | null | undefined
): string | null => {
  if (!dateStr) return null;
  try {
    const d = dayjs(dateStr);
    if (!d.isValid()) return null;
    return d.toISOString();
  } catch {
    return null;
  }
};

/**
 * Safely parse a date input to dayjs object
 * Returns null if invalid
 */
export const toDayjs = (
  date: string | Date | null | undefined
): dayjs.Dayjs | null => {
  if (!date) return null;
  const d = dayjs(date);
  return d.isValid() ? d : null;
};

/**
 * Calculate comparison period dates based on type and primary period.
 * Used for period-over-period comparisons in reports.
 * 
 * @param type - The comparison type
 * @param primaryFrom - Start date of the primary period (string or Date)
 * @param primaryTo - End date of the primary period (string or Date)
 * @param customFrom - Custom start date when type is "custom"
 * @returns Object with dateFrom and dateTo as ISO strings, or null values
 * 
 * @example
 * // Previous period (same length, immediately before)
 * getComparisonPeriodDates("previous", "2025-01-15", "2025-01-31")
 * // Returns: { dateFrom: "2024-12-30T...", dateTo: "2025-01-14T..." }
 * 
 * // Previous month (same dates shifted back one month)
 * getComparisonPeriodDates("previousMonth", "2025-02-01", "2025-02-28")
 * // Returns: { dateFrom: "2025-01-01T...", dateTo: "2025-01-28T..." }
 * 
 * // Same month last year
 * getComparisonPeriodDates("sameMonthLastYear", "2025-03-01", "2025-03-31")
 * // Returns: { dateFrom: "2024-03-01T...", dateTo: "2024-03-31T..." }
 */
export const getComparisonPeriodDates = (
  type: PeriodComparisonType,
  primaryFrom: string | Date | null | undefined,
  primaryTo: string | Date | null | undefined,
  customFrom?: string | Date | null
): { dateFrom: string | null; dateTo: string | null } => {
  const from = toDayjs(primaryFrom);
  const to = toDayjs(primaryTo);

  if (!from || !to) {
    return { dateFrom: null, dateTo: null };
  }

  const periodDays = to.diff(from, "day");

  switch (type) {
    case "previous": {
      // Previous period of same length, ending the day before primary starts
      const compTo = from.subtract(1, "day");
      const compFrom = compTo.subtract(periodDays, "day");
      return {
        dateFrom: compFrom.toISOString(),
        dateTo: compTo.toISOString(),
      };
    }

    case "previousMonth": {
      // Same dates shifted back one month
      const compFrom = from.subtract(1, "month");
      const compTo = to.subtract(1, "month");
      return {
        dateFrom: compFrom.toISOString(),
        dateTo: compTo.toISOString(),
      };
    }

    case "sameMonthLastYear": {
      // Same dates shifted back one year
      const compFrom = from.subtract(1, "year");
      const compTo = to.subtract(1, "year");
      return {
        dateFrom: compFrom.toISOString(),
        dateTo: compTo.toISOString(),
      };
    }

    case "custom": {
      // Custom start date with same period length
      const customStart = toDayjs(customFrom);
      if (!customStart) {
        return { dateFrom: null, dateTo: null };
      }
      const compTo = customStart.add(periodDays, "day");
      return {
        dateFrom: customStart.toISOString(),
        dateTo: compTo.toISOString(),
      };
    }

    case "none":
    default:
      return { dateFrom: null, dateTo: null };
  }
};